import { api } from "./api";

/**
 * Buyer credit position (credit control).
 *
 * Two independent rules: outstanding + the sale being made against the buyer's
 * credit limit, and any unpaid invoice past its due date. Enforcement is a
 * per-farm setting — `mode` says which of off / warn / block is in force, and
 * `blocking` says whether THIS breach will actually refuse the sale.
 */

export type CreditMode = "off" | "warn" | "block";

export interface CreditStatus {
  mode: CreditMode;
  /** false when either rule is breached, whatever the mode. */
  ok: boolean;
  /**
   * true only when the breach will actually refuse the sale. The server shares
   * this one field with its own gate, so the form can never claim a block the
   * save won't hit. False in warn mode, and false when the sale takes no credit
   * — a breaching buyer paying at the counter is reported, not refused.
   */
  blocking: boolean;
  over_limit: boolean;
  overdue: boolean;
  /** Unpaid balance BEFORE the sale being made, already net of advance credit. */
  outstanding: number;
  /** Unapplied money the buyer has already paid, netted off above. */
  advance_credit: number;
  /** outstanding + the sale being attempted. */
  projected: number;
  credit_limit: number | null;
  credit_days: number;
  overdue_amount: number;
  oldest_overdue_days: number;
}

export const creditControlService = {
  /**
   * Where a buyer stands, optionally including a sale about to be made.
   * Read-only — the sale modals call this as the amount changes so the seller
   * sees the position before committing, not after.
   */
  get: (buyerId: number, amount?: number) =>
    api
      .get<CreditStatus>(
        `/buyers/${buyerId}/credit${amount != null && amount > 0 ? `?amount=${amount}` : ""}`,
      )
      .then((r) => r.data),
};

/** The breach in the seller's words. Empty when nothing is wrong. */
export function creditReason(s: CreditStatus): string {
  const inr = (v: number) => `₹${Math.round(v).toLocaleString("en-IN")}`;
  const parts: string[] = [];
  if (s.over_limit) {
    parts.push(
      s.credit_limit == null
        ? `${inr(s.projected)} outstanding after this sale`
        : `${inr(s.projected)} outstanding after this sale, against a ${inr(s.credit_limit)} limit`,
    );
  }
  if (s.overdue) {
    parts.push(
      `${inr(s.overdue_amount)} overdue — oldest ${s.oldest_overdue_days} days against ${s.credit_days}-day terms`,
    );
  }
  // Explain the netting when it happened, or "₹0 outstanding" against a pile of
  // visible invoices reads as a bug.
  if (s.advance_credit > 0) {
    parts.push(`${inr(s.advance_credit)} credit already paid has been counted`);
  }
  return parts.join(" · ");
}
